import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck, User, LayoutDashboard } from "lucide-react";
import { LOGO_URL, useAuth } from "@/lib/api";

const NAV_ITEMS = [
    { to: "/", label: "Accueil" },
    { to: "/expertise-pa", label: "Expertise PA" },
    { to: "/diversification-activite", label: "Diversification d'activité" },
    { to: "/modele-economique", label: "Modèle économique" },
    { to: "/sessions", label: "Sessions" },
    { to: "/nos-formateurs", label: "Nos formateurs" },
    { to: "/notre-comite-scientifique", label: "Comité scientifique" },
    { to: "/actualite-pa", label: "L'actualité PA" },
    { to: "/contact", label: "Contact" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const isHome = location.pathname === "/";
    const { user } = useAuth();

    const dashboardLink = user
        ? user.role === "admin"
            ? "/admin"
            : user.role === "formateur"
                ? (user.must_change_password || !user.onboarding_completed ? "/formateur/onboarding" : "/formateur")
                : "/stagiaire"
        : "/login";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const transparent = isHome && !scrolled;

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                transparent
                    ? "bg-transparent border-b border-transparent"
                    : "bg-white/85 backdrop-blur-md border-b border-brand-line shadow-[0_1px_0_rgba(10,10,10,0.03)]"
            }`}
        >
            <div
                className={`hidden md:flex justify-center border-b text-[11px] tracking-wide uppercase ${
                    transparent
                        ? "border-white/10 text-white/70"
                        : "border-brand-line text-brand-smoke"
                }`}
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2 w-full">
                    <span className="inline-flex items-center gap-2">
                        <ShieldCheck className="h-3.5 w-3.5 text-brand-orange" />
                        L'Institut de la <span className="font-semibold text-brand-orange">Prévention Active</span>
                    </span>
                    <span className="hidden lg:inline">Organisme Qualiopi · contact@cursus-physio.fr</span>
                </div>
            </div>

            <div className="mx-auto flex max-w-screen-2xl items-center justify-between px-6 py-4 lg:px-10">
                <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
                    <img
                        src={LOGO_URL}
                        alt="Cursus Physio"
                        className={`h-10 w-auto transition-all ${transparent ? "brightness-0 invert" : ""}`}
                    />
                </Link>

                <nav className="hidden 2xl:flex items-center gap-x-7 text-[13px]">
                    {NAV_ITEMS.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `hover-underline text-sm font-medium tracking-tight transition-colors ${
                                    transparent
                                        ? isActive
                                            ? "text-brand-orange"
                                            : "text-white/90 hover:text-white"
                                        : isActive
                                        ? "text-brand-orange"
                                        : "text-brand-ink/80 hover:text-brand-ink"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Link
                        to={dashboardLink}
                        data-testid="header-login-link"
                        className={`hidden md:inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold transition-all ${
                            transparent
                                ? "border-white/25 text-white/90 hover:border-white/60 hover:text-white"
                                : "border-brand-ink/20 text-brand-ink hover:border-brand-ink"
                        }`}
                    >
                        {user ? <LayoutDashboard className="h-4 w-4" /> : <User className="h-4 w-4" />}
                        {user ? "Mon espace" : "Se connecter"}
                    </Link>
                    <Link
                        to="/sessions"
                        data-testid="header-cta-inscription"
                        className="hidden md:inline-flex items-center rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#d9772c] hover:shadow-md"
                    >
                        S'inscrire
                    </Link>

                    <Link
                        to={dashboardLink}
                        aria-label={user ? "Mon espace" : "Se connecter"}
                        data-testid="header-mobile-login-icon"
                        className={`inline-flex md:hidden h-10 w-10 items-center justify-center rounded-full border transition-colors ${
                            transparent
                                ? "border-white/30 text-white hover:border-white/70"
                                : "border-brand-line text-brand-ink hover:border-brand-ink"
                        }`}
                    >
                        {user ? <LayoutDashboard className="h-4 w-4" /> : <User className="h-4 w-4" />}
                    </Link>

                    <button
                        type="button"
                        data-testid="header-mobile-toggle"
                        className={`inline-flex 2xl:hidden h-10 w-10 items-center justify-center rounded-full border transition-colors ${
                            transparent
                                ? "border-white/30 text-white"
                                : "border-brand-line text-brand-ink"
                        }`}
                        aria-label="Ouvrir le menu"
                        onClick={() => setMobileOpen((o) => !o)}
                    >
                        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div className="2xl:hidden border-t border-brand-line bg-white" data-testid="mobile-menu">
                    <nav className="mx-auto max-w-7xl px-6 py-6 flex flex-col gap-1">
                        {NAV_ITEMS.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `py-3 text-base font-medium border-b border-brand-line/60 ${
                                        isActive ? "text-brand-orange" : "text-brand-ink"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}

                        {/* Account block — pinned bottom of drawer */}
                        <div className="mt-5 space-y-2.5">
                            <Link
                                to={dashboardLink}
                                data-testid="mobile-login-link"
                                className="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-brand-ink/15 bg-white px-5 py-3.5 text-sm font-semibold text-brand-ink transition-colors hover:border-brand-ink"
                            >
                                {user ? <LayoutDashboard className="h-4 w-4" /> : <User className="h-4 w-4" />}
                                {user ? "Mon espace" : "Se connecter"}
                            </Link>
                            <Link
                                to="/sessions"
                                data-testid="mobile-cta-inscription"
                                className="inline-flex w-full items-center justify-center rounded-full bg-brand-orange px-5 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-[#d9772c]"
                            >
                                S'inscrire à une session
                            </Link>
                            {user && (
                                <p className="pt-1 text-center text-[11px] text-brand-smoke">
                                    Connecté · {user.email}
                                </p>
                            )}
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
}
